import {
    useLayoutEffect,
    useEffect,
    type FC,
    useId
} from "react";
import {
    type GestureResponderEvent,
    Platform,
    View
} from "react-native";
import type ILabelProps from "./type";
import type {
    ILabelBaseProps
} from "./type";
import stylesheet, {
    getLabelTypography,
    getLabelColors,
    useStyles
} from "./stylesheet";
import {
    registerLabelText
} from "./store";
import {
    activateLabelControl,
    linkLabelControl
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    getFieldChildrenText
} from "../field/util";
import Text from "../text";

export const LabelBase: FC<ILabelBaseProps> = ({
    isDisabled = false,
    isOptional = false,
    isRequired = false,
    variant = "label",
    size = "medium",
    customLocalize,
    isRow = false,
    optionalText,
    customTheme,
    titleColor,
    children,
    htmlFor,
    onPress,
    style,
    id,
    ...props
}) => {
    const {
        inlineSpaces,
        colors
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const isWeb = Platform.OS === "web";

    const isLinked = isWeb && !!htmlFor;

    const labelID = id ?? (isLinked ? `${baseID}-label` : undefined);

    const isOptionalVisible = isOptional || !!optionalText;

    const isRowVisible = isRow || isRequired || isOptionalVisible;

    const isPressable = !isDisabled && (!!onPress || isLinked);

    const labelText = getFieldChildrenText(children);

    const currentColors = getLabelColors({
        isDisabled,
        titleColor,
        colors
    });

    const currentTypography = getLabelTypography({
        variant,
        size
    });

    const {
        optionalText: optionalTextDynamicStyle,
        required: requiredDynamicStyle
    } = useStyles({
        inlineSpaces
    });

    useLayoutEffect(() => {
        if(!htmlFor || !labelText) {
            return;
        }

        return registerLabelText({
            text: labelText,
            htmlFor
        });
    }, [
        labelText,
        htmlFor
    ]);

    useEffect(() => {
        if(!isLinked || !htmlFor || !labelID) {
            return;
        }

        return linkLabelControl({
            labelID,
            htmlFor
        });
    });

    const handlePress = (event: GestureResponderEvent) => {
        if(isLinked && htmlFor && activateLabelControl(htmlFor)) {
            return;
        }

        onPress?.(event);
    };

    const currentOnPress = isPressable ? handlePress : undefined;

    const renderRequired = () => {
        if(!isRequired) {
            return null;
        }

        return <Text
            customColor={currentColors.requiredColor}
            variant={currentTypography.title}
            style={requiredDynamicStyle}
            customTheme={customTheme}
            onPress={currentOnPress}
            aria-hidden={true}
        >
            *
        </Text>;
    };

    const renderOptionalText = () => {
        if(!isOptionalVisible) {
            return null;
        }

        return <Text
            customColor={currentColors.optionalTextColor}
            style={optionalTextDynamicStyle}
            variant={currentTypography.note}
            customTheme={customTheme}
            onPress={currentOnPress}
        >
            {isOptional ? localize("is-optional") : optionalText}
        </Text>;
    };

    if(!isRowVisible) {
        return <Text
            {...props}
            customColor={currentColors.titleColor}
            variant={currentTypography.title}
            customTheme={customTheme}
            onPress={currentOnPress}
            style={style}
            id={labelID}
        >
            {children}
        </Text>;
    }

    return <View
        style={stylesheet.container}
        id={labelID}
    >
        <Text
            {...props}
            customColor={currentColors.titleColor}
            variant={currentTypography.title}
            customTheme={customTheme}
            onPress={currentOnPress}
            style={[
                style,
                stylesheet.title
            ]}
        >
            {children}
        </Text>
        {renderRequired()}
        {renderOptionalText()}
    </View>;
};

const Label: FC<ILabelProps> = (props) => {
    return <LabelBase
        {...props}
    />;
};
export default Label;
